import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import type { IconType } from "../../../icon";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";
import { FilterSectionHeader } from "../../FilterSectionHeader";
import {
	getSectionStatus,
	hasEnabledOptions,
} from "../../FilterSectionHeader/helpers";
import OptionListItem from "../../QuickStudyModal/OptionListItem";
import SkillsGrid from "../../QuickStudyModal/SkillsGrid";
import type { ReviewAnswersAction, ReviewAnswersView } from "./types";

type Props = {
	view: ReviewAnswersView;
	loading?: boolean;
	toggle: (action: ReviewAnswersAction) => void;
	/** "sheet" renders inside a BottomSheetModalWrapper, which already provides
	 * its own scroll view — nesting another one breaks sheet gestures, so
	 * sticky section headers are dialog-only. */
	presentation?: "dialog" | "sheet";
};

type CollapsibleFilterSection =
	| "questionTypes"
	| "proficiencies"
	| "performance";

const DEFAULT_SKILL_ICON: IconType = "book-open-page-variant-outline";

const toggleCollapsedSection = (
	current: Set<CollapsibleFilterSection>,
	section: CollapsibleFilterSection,
) => {
	const next = new Set(current);
	if (next.has(section)) {
		next.delete(section);
	} else {
		next.add(section);
	}
	return next;
};

const getStickyHeaderIndices = (
	sections: Array<{ hasOptions: boolean; expanded: boolean }>,
) => {
	let childIndex = 0;

	return sections.reduce<number[]>((indices, { hasOptions, expanded }) => {
		if (hasOptions) {
			indices.push(childIndex);
			childIndex += 1 + Number(expanded);
		}
		return indices;
	}, []);
};

export const SessionReportReviewAnswersFilterSections = ({
	view,
	loading,
	toggle,
	presentation = "dialog",
}: Props) => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const isInteractionDisabled = Boolean(loading);
	const [collapsedSections, setCollapsedSections] = useState<
		Set<CollapsibleFilterSection>
	>(() => new Set());
	const isSectionExpanded = (section: CollapsibleFilterSection) =>
		!collapsedSections.has(section);
	const toggleSectionExpanded = (section: CollapsibleFilterSection) => {
		setCollapsedSections((current) => toggleCollapsedSection(current, section));
	};

	const questionTypesStatus = getSectionStatus(view.questionTypes);
	const proficienciesStatus = getSectionStatus(view.proficiencies);
	const performanceStatus = getSectionStatus(view.performance);

	const hasSkills = view.skills.length > 1;
	const hasQuestionTypes = view.questionTypes.length > 0;
	const hasProficiencies = view.proficiencies.length > 0;
	const hasPerformance = view.performance.length > 0;
	const questionTypesExpanded = isSectionExpanded("questionTypes");
	const proficienciesExpanded = isSectionExpanded("proficiencies");
	const performanceExpanded = isSectionExpanded("performance");
	const questionTypesSelectedCount = view.questionTypes.filter(
		(option) => option.checked,
	).length;
	const proficienciesSelectedCount = view.proficiencies.filter(
		(option) => option.checked,
	).length;
	const performanceSelectedCount = view.performance.filter(
		(option) => option.checked,
	).length;

	const stickyHeaderIndices = getStickyHeaderIndices([
		{ hasOptions: hasQuestionTypes, expanded: questionTypesExpanded },
		{ hasOptions: hasProficiencies, expanded: proficienciesExpanded },
		{ hasOptions: hasPerformance, expanded: performanceExpanded },
	]);

	const skillsBlock = hasSkills ? (
		<Box paddingHorizontal={24} paddingBottom={12}>
			<SkillsGrid
				selectionMode="multiple"
				selectedIds={view.skills
					.filter((skill) => skill.checked)
					.map((skill) => String(skill.id))}
				skills={view.skills.map((skill) => ({
					id: String(skill.id),
					label: skill.label,
					icon: skill.icon ?? DEFAULT_SKILL_ICON,
					disabled: skill.disabled,
				}))}
				disabled={isInteractionDisabled}
				onSelect={(id) => toggle({ facet: "skill", id: Number(id) })}
			/>
		</Box>
	) : null;

	const sectionsContent = (
		<>
			{hasQuestionTypes ? (
				<Box
					paddingHorizontal={24}
					paddingBottom={6}
					backgroundColor={theme.colors.surface}
				>
					<FilterSectionHeader
						title={t("report.reviewAnswersDialog.sections.questionTypes")}
						status={questionTypesStatus}
						selectedCount={questionTypesSelectedCount}
						expanded={questionTypesExpanded}
						disabled={
							isInteractionDisabled || !hasEnabledOptions(view.questionTypes)
						}
						onToggle={() =>
							toggle({
								facet: "questionType",
								setAll: questionTypesStatus !== "checked",
							})
						}
						onToggleExpanded={() => toggleSectionExpanded("questionTypes")}
					/>
				</Box>
			) : null}
			{hasQuestionTypes && questionTypesExpanded ? (
				<Box paddingHorizontal={24} paddingBottom={12} gap={2}>
					{view.questionTypes.map((option) => (
						<OptionListItem
							key={option.id}
							id={String(option.id)}
							label={option.label}
							icon={option.icon}
							countLabel={t("common.itemCount", {
								count: option.count,
							})}
							checked={option.checked}
							disabled={option.disabled || isInteractionDisabled}
							onToggle={() => toggle({ facet: "questionType", id: option.id })}
						/>
					))}
				</Box>
			) : null}

			{hasProficiencies ? (
				<Box
					paddingHorizontal={24}
					paddingBottom={6}
					backgroundColor={theme.colors.surface}
				>
					<FilterSectionHeader
						title={t("report.reviewAnswersDialog.sections.proficiency")}
						status={proficienciesStatus}
						selectedCount={proficienciesSelectedCount}
						expanded={proficienciesExpanded}
						disabled={
							isInteractionDisabled || !hasEnabledOptions(view.proficiencies)
						}
						onToggle={() =>
							toggle({
								facet: "proficiency",
								setAll: proficienciesStatus !== "checked",
							})
						}
						onToggleExpanded={() => toggleSectionExpanded("proficiencies")}
					/>
				</Box>
			) : null}
			{hasProficiencies && proficienciesExpanded ? (
				<Box paddingHorizontal={24} paddingBottom={12} gap={2}>
					{view.proficiencies.map((option) => (
						<OptionListItem
							key={option.id}
							id={String(option.id)}
							label={option.label}
							icon={option.icon}
							countLabel={t("common.itemCount", {
								count: option.count,
							})}
							checked={option.checked}
							disabled={option.disabled || isInteractionDisabled}
							onToggle={() => toggle({ facet: "proficiency", id: option.id })}
						/>
					))}
				</Box>
			) : null}

			{hasPerformance ? (
				<Box
					paddingHorizontal={24}
					paddingBottom={6}
					backgroundColor={theme.colors.surface}
				>
					<FilterSectionHeader
						title={t("report.reviewAnswersDialog.sections.performance")}
						status={performanceStatus}
						selectedCount={performanceSelectedCount}
						expanded={performanceExpanded}
						disabled={
							isInteractionDisabled || !hasEnabledOptions(view.performance)
						}
						onToggle={() =>
							toggle({
								facet: "performance",
								setAll: performanceStatus !== "checked",
							})
						}
						onToggleExpanded={() => toggleSectionExpanded("performance")}
					/>
				</Box>
			) : null}
			{hasPerformance && performanceExpanded ? (
				<Box paddingHorizontal={24} paddingBottom={12} gap={2}>
					{view.performance.map((option) => (
						<OptionListItem
							key={option.id}
							id={option.id}
							label={option.label}
							icon={option.icon}
							countLabel={t("common.itemCount", {
								count: option.count,
							})}
							checked={option.checked}
							disabled={option.disabled || isInteractionDisabled}
							onToggle={() => toggle({ facet: "performance", id: option.id })}
						/>
					))}
				</Box>
			) : null}
		</>
	);

	if (presentation === "sheet") {
		return (
			<>
				{skillsBlock}
				{sectionsContent}
			</>
		);
	}

	return (
		<>
			{skillsBlock}
			<ScrollView
				showsVerticalScrollIndicator={false}
				stickyHeaderIndices={stickyHeaderIndices}
				contentContainerStyle={{ paddingBottom: 4 }}
			>
				{sectionsContent}
			</ScrollView>
		</>
	);
};
